import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import InfiniteLoader from '@/components/InfiniteLoader';
import InfoTooltip from '@/components/InfoTooltip';
import { SrOnly } from '@/components/Layout/Parts';
import { ResponsiveTable, Table, TableContent, Th } from '@/components/Table';
import {
  FreelancerHolidayRequestResource,
  PaginatedResponse,
} from '@/types/resources';
import { api } from '@/utils/api';

import FreelancersVacationTableRow from './FreelancersVacationTableRow';

const StyledTable = styled(Table)`
  th {
    font-size: 11px;
  }
  th,
  td {
    padding: 19px 16px;
    font-size: 12px;
    font-weight: 700;
    &:nth-child(1) {
      padding-left: 24px;
    }
  }
  th {
    &:last-child {
      width: 56px;
    }
  }
  td {
    &:last-child {
      width: 56px;
      height: 64px;
      position: relative;
      padding-right: 16px;
      padding-left: 16px;
      border-left: 1px solid ${({ theme }) => theme.colors.grayscale} !important;
    }
  }
`;

const FreelancersVacationTable = () => {
  const { id } = useParams();

  const fetchRequests = ({
    pageParam = '/v1/payments/freelancers/holidays/requests',
  }) => {
    return api.get<never, PaginatedResponse<FreelancerHolidayRequestResource>>(
      pageParam,
      {
        params: { 'filter[freelancer_id]': id },
      },
    );
  };

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<FreelancerHolidayRequestResource>>(
    ['freelancer_vacation_requests', { freelancer_id: id }],
    fetchRequests,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
    },
  );

  const requests = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<FreelancerHolidayRequestResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        [],
      ),
    [data],
  );

  return (
    <>
      <ResponsiveTable>
        <StyledTable>
          <thead>
            <tr>
              <Th>Request Date</Th>
              <Th>Vacation Date</Th>
              <Th>Deducted Days</Th>
              <Th>
                Type
                <InfoTooltip
                  title="Type"
                  content="Paid leave uses your available balance. Unpaid leave is uncompensated"
                />
              </Th>
              <Th>Team Lead Approval</Th>
              <Th>HR Approval</Th>
              <Th>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={7}
              isError={isError}
              hasData={!!requests && requests.length > 0}
            >
              {requests?.map((request) => (
                <FreelancersVacationTableRow
                  key={request.id}
                  request={request}
                />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default FreelancersVacationTable;
